// src/components/AdminRoles.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../utils/auth';
import { API_URL } from '../config';
import s from './AdminPanel.module.css';
import actionsIcon from '../icons/admin_panel_actions.svg';
import { SearchInput, Select, Input, Textarea, ActionsMenu } from './UI';
import actionsMenuStyles from './UI/ActionsMenu.module.css';
import { ReactComponent as CloseIcon } from '../icons/close_button.svg';
import { ReactComponent as AddIconPlus } from '../icons/add_icon_plus.svg';
import { ReactComponent as FabBubble } from '../icons/circle.svg';

function toErrorText(err, fallback = 'Не удалось выполнить действие') {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  if (err?.detail) {
    if (typeof err.detail === 'string') return err.detail;
    if (Array.isArray(err.detail)) {
      return err.detail.map((x) => x?.msg || String(x)).join(', ');
    }
  }
  return err?.message || fallback;
}

async function readError(res, fallback) {
  try {
    const data = await res.json();
    return toErrorText(data, fallback);
  } catch {
    return fallback;
  }
}

function groupPermissions(items) {
  const map = new Map();
  for (const p of items || []) {
    const key = (p.group_name || 'Без группы').trim() || 'Без группы';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(p);
  }

  return Array.from(map.entries())
    .map(([group, permissions]) => ({
      group,
      permissions: [...permissions].sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), 'ru')
      ),
    }))
    .sort((a, b) => a.group.localeCompare(b.group, 'ru'));
}

function InnerTabs({ value, onChange, items }) {
  const tabRefs = useRef({});
  const [indicatorStyle, setIndicatorStyle] = useState({
    width: 0,
    transform: 'translateX(0px)',
    opacity: 0,
  });

  useEffect(() => {
    const updateIndicator = () => {
      const activeEl = tabRefs.current?.[value];
      if (!activeEl) {
        setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
        return;
      }

      setIndicatorStyle({
        width: activeEl.offsetWidth,
        transform: `translateX(${activeEl.offsetLeft}px)`,
        opacity: 1,
      });
    };

    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [value, items]);

  return (
    <div className={s.tabs} role="tablist" aria-label="Подвкладки ролей и доступов">
      {items.map((item) => (
        <button
          key={item.key}
          ref={(el) => {
            if (el) tabRefs.current[item.key] = el;
          }}
          type="button"
          role="tab"
          aria-selected={value === item.key}
          className={`${s.tab} ${value === item.key ? s.tabActive : ''}`}
          onClick={() => onChange(item.key)}
        >
          {item.label}
        </button>
      ))}
      <span
        className={s.tabIndicator}
        aria-hidden="true"
        style={indicatorStyle}
      />
    </div>
  );
}

function RoleModal({ title, initialValue, saving, onCancel, onSubmit }) {
  const [form, setForm] = useState({
    name: initialValue?.name || '',
    description: initialValue?.description || '',
  });

  const isSystem = !!initialValue?.is_system;
  const canSubmit = form.name.trim();

  useEffect(() => {
    setForm({
      name: initialValue?.name || '',
      description: initialValue?.description || '',
    });
  }, [initialValue]);

  return (
    <div
      className={s.modalBackdrop}
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className={s.modalCard} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <strong>{title}</strong>
          <button
            type="button"
            className={s.modalClose}
            onClick={onCancel}
            aria-label="Закрыть"
          >
            <CloseIcon className={s.closeIcon} />
          </button>
        </div>

        <div className={s.createForm}>

          <div className={s.fieldHeader}>
            <label>Название роли <span style={{ color: '#C01048' }}>*</span></label>
          </div>
          <Input
            className={s.fieldInput}
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="Название роли"
            ariaLabel="Название роли"
            disabled={saving}
          />

          <div className={s.fieldHeader}>
            <label>Описание</label>
          </div>
          <Textarea
            className={s.fieldTextarea}
            rows={5}
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Короткое описание роли"
            ariaLabel="Описание роли"
            disabled={saving}
          />

          {isSystem ? (
            <div className={s.infoNote}>Системную роль лучше не менять.</div>
          ) : null}
        </div>

        <div className={s.modalFooter}>
          <button type="button" className={s.btnCancel} onClick={onCancel} disabled={saving}>
            Отмена
          </button>
          <button
            type="button"
            className={s.btnPrimary}
            onClick={() =>
              onSubmit({
                name: form.name.trim(),
                description: form.description.trim(),
              })
            }
            disabled={!canSubmit || saving}
          >
            {saving ? 'Сохраняем...' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PermissionModal({ title, initialValue, permissions = [], saving, onCancel, onSubmit }) {
  const [form, setForm] = useState({
    name: initialValue?.name || '',
    description: initialValue?.description || '',
    group_name: initialValue?.group_name || '',
  });

  const isSystem = !!initialValue?.is_system;
  const canSubmit = form.name.trim();

  useEffect(() => {
    setForm({
      name: initialValue?.name || '',
      description: initialValue?.description || '',
      group_name: initialValue?.group_name || '',
    });
  }, [initialValue]);

  const groupOptions = useMemo(() => {
    const set = new Set(
      (permissions || [])
        .map((p) => (p?.group_name || '').trim())
        .filter(Boolean)
    );
    const currentGroup = (form.group_name || '').trim();
    if (currentGroup) set.add(currentGroup);
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ru'));
  }, [permissions, form.group_name]);

  return (
    <div
      className={s.modalBackdrop}
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className={s.modalCard} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <strong>{title}</strong>
          <button
            type="button"
            className={s.modalClose}
            onClick={onCancel}
            aria-label="Закрыть"
          >
            <CloseIcon className={s.closeIcon} />
          </button>
        </div>

        <div className={s.createForm}>

          <div className={s.fieldHeader}>
            <label>Название <span style={{ color: '#C01048' }}>*</span></label>
          </div>
          <Input
            className={s.fieldInput}
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="Название доступа"
            ariaLabel="Название доступа"
            disabled={saving}
          />

          <div className={s.fieldHeader}>
            <label>Группа</label>
          </div>
          <Select
            className={s.fieldInput}
            value={form.group_name}
            onChange={(e) => setForm((prev) => ({ ...prev, group_name: e.target.value }))}
            ariaLabel="Группа доступа"
            disabled={saving}
          >
            <option value="">Без группы</option>
            {groupOptions.map((group) => (
              <option key={group} value={group}>
                {group}
              </option>
            ))}
          </Select>

          <div className={s.fieldHeader}>
            <label>Описание</label>
          </div>
          <Textarea
            className={s.fieldTextarea}
            rows={5}
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Описание доступа"
            ariaLabel="Описание доступа"
            disabled={saving}
          />

          {isSystem ? (
            <div className={s.infoNote}>Системный доступ лучше не менять.</div>
          ) : null}
        </div>

        <div className={s.modalFooter}>
          <button type="button" className={s.btnCancel} onClick={onCancel} disabled={saving}>
            Отмена
          </button>
          <button
            type="button"
            className={s.btnPrimary}
            onClick={() =>
              onSubmit({
                code: form.code.trim(),
                name: form.name.trim(),
                description: form.description.trim(),
                group_name: form.group_name.trim() || null,
              })
            }
            disabled={!canSubmit || saving}
          >
            {saving ? 'Сохраняем...' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RolePermissionsModal({
  role,
  permissions,
  saving,
  onCancel,
  onSubmit,
}) {
  const ALL_GROUP_KEY = '__all__';
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeGroup, setActiveGroup] = useState('');
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(true);

  useEffect(() => {
    const ids = Array.isArray(role?.permissions) ? role.permissions.map((p) => p.id) : [];
    setSelectedIds(ids);
  }, [role]);

  const filteredPermissions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return permissions || [];
    return (permissions || []).filter((p) => {
      const hay = `${p.name || ''} ${p.group_name || ''} ${p.description || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [permissions, search]);

  const allGroups = useMemo(() => groupPermissions(permissions || []), [permissions]);
  const filteredGroups = useMemo(() => groupPermissions(filteredPermissions), [filteredPermissions]);
  const filteredGroupMap = useMemo(
    () => new Map(filteredGroups.map((g) => [g.group, g.permissions])),
    [filteredGroups]
  );

  useEffect(() => {
    if (!allGroups.length) {
      setActiveGroup('');
      return;
    }
    setActiveGroup((prev) => {
      if (prev === ALL_GROUP_KEY) return prev;
      if (prev && allGroups.some((g) => g.group === prev)) return prev;
      return ALL_GROUP_KEY;
    });
  }, [allGroups, ALL_GROUP_KEY]);

  const togglePermission = (permissionId) => {
    setSelectedIds((prev) => (
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    ));
  };

  const setGroupPermissions = (permissionIds, checked) => {
    setSelectedIds((prev) => {
      const groupSet = new Set(permissionIds);
      if (checked) {
        const merged = new Set(prev);
        permissionIds.forEach((id) => merged.add(id));
        return Array.from(merged);
      }
      return prev.filter((id) => !groupSet.has(id));
    });
  };

  const activeGroupAllPermissions = useMemo(() => {
    if (activeGroup === ALL_GROUP_KEY) return permissions || [];
    const group = allGroups.find((g) => g.group === activeGroup);
    return group?.permissions || [];
  }, [allGroups, activeGroup, permissions, ALL_GROUP_KEY]);

  const activeGroupVisiblePermissions = useMemo(
    () => (activeGroup === ALL_GROUP_KEY ? filteredPermissions : (filteredGroupMap.get(activeGroup) || [])),
    [filteredGroupMap, activeGroup, filteredPermissions, ALL_GROUP_KEY]
  );

  const selectedCount = useMemo(() => {
    const validIds = new Set((permissions || []).map((p) => p.id));
    return selectedIds.filter((id) => validIds.has(id)).length;
  }, [permissions, selectedIds]);

  const allInCurrentScopeSelected = useMemo(() => {
    if (!activeGroupAllPermissions.length) return false;
    return activeGroupAllPermissions.every((perm) => selectedIds.includes(perm.id));
  }, [activeGroupAllPermissions, selectedIds]);

  return (
    <div
      className={s.modalBackdrop}
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className={s.modalCard} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <strong>Доступы роли: {role?.name || '—'}</strong>
          <button
            type="button"
            className={s.modalClose}
            onClick={onCancel}
            aria-label="Закрыть"
          >
            <CloseIcon className={s.closeIcon} />
          </button>
        </div>

        <div className={s.createForm}>
          <div className={s.permissionsLayout}>
            <aside className={s.permissionsSidebar}>
              <div className={s.permissionsSidebarHeader}>
                <div className={s.permissionsSidebarTitle}>Категории</div>
                <button
                  type="button"
                  className={s.permissionsSidebarToggle}
                  onClick={() => setIsCategoriesOpen((prev) => !prev)}
                  aria-expanded={isCategoriesOpen}
                  aria-controls="permissions-categories-list"
                  aria-label={isCategoriesOpen ? 'Свернуть категории' : 'Развернуть категории'}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`${s.permissionsSidebarChevron} ${isCategoriesOpen ? s.permissionsSidebarChevronOpen : ''}`}
                    aria-hidden="true"
                  >
                    <path
                      d="M6 9L12 15L18 9"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
              <div
                id="permissions-categories-list"
                className={`${s.permissionsCategoryList} ${!isCategoriesOpen ? s.permissionsCategoryListCollapsed : ''}`}
              >
                <button
                  type="button"
                  className={`${s.permissionsCategoryBtn} ${activeGroup === ALL_GROUP_KEY ? s.permissionsCategoryBtnActive : ''}`}
                  onClick={() => setActiveGroup(ALL_GROUP_KEY)}
                  disabled={saving}
                >
                  <span>Все</span>
                  <span className={s.permissionsCategoryCount}>{selectedCount}/{(permissions || []).length}</span>
                </button>
                {allGroups.map((group) => {
                  const total = group.permissions.length;
                  const selected = group.permissions.filter((perm) => selectedIds.includes(perm.id)).length;
                  const isActive = group.group === activeGroup;
                  return (
                    <button
                      key={group.group}
                      type="button"
                      className={`${s.permissionsCategoryBtn} ${isActive ? s.permissionsCategoryBtnActive : ''}`}
                      onClick={() => setActiveGroup(group.group)}
                      disabled={saving}
                    >
                      <span>{group.group}</span>
                      <span className={s.permissionsCategoryCount}>{selected}/{total}</span>
                    </button>
                  );
                })}
              </div>
            </aside>

            <section className={s.permissionsMain}>
              <SearchInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Поиск доступа..."
                ariaLabel="Поиск доступа"
              />

              <div className={s.permissionsList}>
                {activeGroupVisiblePermissions.length === 0 ? (
                  <div className={s.note}>
                    {search.trim()
                      ? 'По запросу ничего не найдено.'
                      : activeGroup === ALL_GROUP_KEY
                        ? 'Доступы отсутствуют.'
                        : 'В категории нет доступов.'}
                  </div>
                ) : (
                  <>
                    <label className={`${s.checkboxRow} ${s.checkboxRowAll}`}>
                      <input
                        type="checkbox"
                        checked={allInCurrentScopeSelected}
                        onChange={(e) => setGroupPermissions(
                          activeGroupAllPermissions.map((perm) => perm.id),
                          e.target.checked
                        )}
                        disabled={saving || activeGroupAllPermissions.length === 0}
                      />
                      <span>
                        <strong>Все</strong>
                      </span>
                    </label>

                    {activeGroupVisiblePermissions.map((perm) => {
                      const checked = selectedIds.includes(perm.id);
                      return (
                        <label key={perm.id} className={s.checkboxRow}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => togglePermission(perm.id)}
                            disabled={saving}
                          />
                          <span>
                            <strong>{perm.name}</strong>
                            {perm.description ? (
                              <span className={s.metaMultiline} style={{ display: 'block' }}>
                                {perm.description}
                              </span>
                            ) : null}
                          </span>
                        </label>
                      );
                    })}
                  </>
                )}
              </div>

              <div className={s.permissionsBulkBar}>
                <span className={s.permissionsSelectedInfo}>
                  Выбрано: {selectedCount} из {(permissions || []).length}
                </span>
              </div>
            </section>
          </div>
        </div>

        <div className={s.modalFooter}>
          <button type="button" className={s.btnCancel} onClick={onCancel} disabled={saving}>
            Отмена
          </button>
          <button
            type="button"
            className={s.btnPrimary}
            onClick={() => onSubmit({ permission_ids: selectedIds })}
            disabled={saving}
          >
            {saving ? 'Сохраняем...' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RolesList({
  loading,
  roles,
  search,
  setSearch,
  activeRoleId,
  setActiveRoleId,
  roleMenuId,
  setRoleMenuId,
  openRolePermissions,
  openEditRole,
  handleDeleteRole,
}) {
  const filteredRoles = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter((r) => {
      const hay = `${r.name || ''} ${r.description || ''} ${r.is_system ? 'системная роль' : 'пользовательская роль'}`.toLowerCase();
      return hay.includes(q);
    });
  }, [roles, search]);

  return (
    <div className={s.section}>
      <SearchInput
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Поиск роли"
        ariaLabel="Поиск роли"
      />

      <ul className={s.list}>
        {loading ? (
          <li className={s.row}>
            <div className={s.main}>
              <div className={s.name}>Загрузка...</div>
            </div>
          </li>
        ) : filteredRoles.length === 0 ? (
          <li className={s.row}>
            <div className={s.main}>
              <div className={s.meta}>Роли не найдены</div>
            </div>
          </li>
        ) : (
          filteredRoles.map((role) => {
            return (
              <li
                key={role.id}
                className={`${s.row} ${s.rowInteractive}`}
                onClick={() => setActiveRoleId(role.id)}
              >
                <div className={s.main}>
                  <div className={s.roleTitleRow}>
                    <div className={s.name}>{role.name}</div>
                    <div className={`${s.roleTypePill} ${role.is_system ? s.roleTypePillSystem : s.roleTypePillUser}`}>
                      {role.is_system ? 'Системная роль' : 'Пользовательская роль'}
                    </div>
                  </div>
                  {role.description ? (
                    <div className={s.metaMultiline}>{role.description}</div>
                  ) : null}
                </div>

                <ActionsMenu
                  open={roleMenuId === role.id}
                  onToggle={(e) => {
                    e.stopPropagation();
                    setRoleMenuId((prev) => (prev === role.id ? null : role.id));
                  }}
                  title="Действия с ролью"
                  align="left"
                  trigger={<img src={actionsIcon} alt="" className={s.iconDots} />}
                  wrapClassName={s.actions}
                  buttonClassName={s.iconBtn}
                  items={[
                    {
                      key: 'permissions',
                      label: 'Доступы роли',
                      onClick: () => openRolePermissions(role),
                    },
                    {
                      key: 'edit',
                      label: 'Редактировать',
                      onClick: () => openEditRole(role),
                    },
                    ...(!role.is_system ? [{
                      key: 'delete',
                      label: 'Удалить',
                      danger: true,
                      onClick: () => handleDeleteRole(role),
                    }] : []),
                  ]}
                />
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}

function PermissionsList({
  loading,
  permissions,
  search,
  setSearch,
  permissionMenuId,
  setPermissionMenuId,
  openEditPermission,
  handleDeletePermission,
}) {
  const filteredPermissions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return permissions;
    return permissions.filter((p) => {
      const hay = `${p.name || ''} ${p.description || ''} ${p.is_system ? 'системный доступ' : 'доступ'}`.toLowerCase();
      return hay.includes(q);
    });
  }, [permissions, search]);

  return (
    <div className={s.section}>
      <SearchInput
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Поиск доступа"
        ariaLabel="Поиск доступа"
      />

      <ul className={s.list}>
        {loading ? (
          <li className={s.row}>
            <div className={s.main}>
              <div className={s.name}>Загрузка...</div>
            </div>
          </li>
        ) : filteredPermissions.length === 0 ? (
          <li className={s.row}>
            <div className={s.main}>
              <div className={s.meta}>Доступы не найдены</div>
            </div>
          </li>
        ) : (
          filteredPermissions.map((permission) => (
            <li key={permission.id} className={s.row}>
              <div className={s.main}>
                <div className={s.roleTitleRow}>
                  <div className={s.name}>{permission.name}</div>
                  {permission.is_system ? (
                    <div className={`${s.roleTypePill} ${s.roleTypePillSystem}`}>
                      Системный доступ
                    </div>
                  ) : null}
                </div>
                {!permission.is_system ? (
                  <div className={s.meta}>доступ</div>
                ) : null}
                {permission.description ? (
                  <div className={s.metaMultiline}>{permission.description}</div>
                ) : null}
              </div>

              <ActionsMenu
                open={permissionMenuId === permission.id}
                onToggle={(e) => {
                  e.stopPropagation();
                  setPermissionMenuId((prev) => (prev === permission.id ? null : permission.id));
                }}
                title="Действия с доступом"
                align="left"
                trigger={<img src={actionsIcon} alt="" className={s.iconDots} />}
                wrapClassName={s.actions}
                buttonClassName={s.iconBtn}
                items={[
                  {
                    key: 'edit',
                    label: 'Редактировать',
                    onClick: () => openEditPermission(permission),
                  },
                  ...(!permission.is_system ? [{
                    key: 'delete',
                    label: 'Удалить',
                    danger: true,
                    onClick: () => handleDeletePermission(permission),
                  }] : []),
                ]}
              />
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

export default function AdminRoles() {
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [roleSearch, setRoleSearch] = useState('');
  const [permissionSearch, setPermissionSearch] = useState('');
  const [, setError] = useState('');
  const [toast, setToast] = useState({ message: '', type: 'success' });
  const toastTimerRef = useRef(null);

  const [subtab, setSubtab] = useState('roles');
  const [activeRoleId, setActiveRoleId] = useState(null);

  const [roleMenuId, setRoleMenuId] = useState(null);
  const [permissionMenuId, setPermissionMenuId] = useState(null);

  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [roleModalMode, setRoleModalMode] = useState('create');
  const [editingRole, setEditingRole] = useState(null);

  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);

  const [rolePermissionsOpen, setRolePermissionsOpen] = useState(false);
  const [roleDetails, setRoleDetails] = useState(null);

  const [saving, setSaving] = useState(false);

  const showToast = useCallback((message, type = 'success') => {
    if (!message) return;
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, type });
    toastTimerRef.current = setTimeout(() => {
      setToast({ message: '', type: 'success' });
    }, 2600);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const loadRoles = useCallback(async () => {
    const res = await apiFetch(`${API_URL}/admin/access/roles`);
    if (!res.ok) {
      throw new Error(await readError(res, 'Не удалось загрузить роли'));
    }
    return res.json();
  }, []);

  const loadPermissions = useCallback(async () => {
    const res = await apiFetch(`${API_URL}/admin/access/permissions`);
    if (!res.ok) {
      throw new Error(await readError(res, 'Не удалось загрузить доступы'));
    }
    return res.json();
  }, []);

  const loadRoleDetails = useCallback(async (roleId) => {
    if (!roleId) return null;
    const res = await apiFetch(`${API_URL}/admin/access/roles/${roleId}`);
    if (!res.ok) {
      throw new Error(await readError(res, 'Не удалось загрузить данные роли'));
    }
    return res.json();
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rolesData, permissionsData] = await Promise.all([loadRoles(), loadPermissions()]);
      setRoles(Array.isArray(rolesData) ? rolesData : []);
      setPermissions(Array.isArray(permissionsData) ? permissionsData : []);

      setActiveRoleId((prev) => {
        if (prev && rolesData.some((r) => String(r.id) === String(prev))) return prev;
        return rolesData[0]?.id ?? null;
      });
    } catch (e) {
      setError(toErrorText(e, 'Не удалось загрузить данные'));
    } finally {
      setLoading(false);
    }
  }, [loadPermissions, loadRoles]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest?.(`.${actionsMenuStyles.wrap}, .${s.actions}`)) {
        setRoleMenuId(null);
        setPermissionMenuId(null);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const openCreateRole = () => {
    setEditingRole(null);
    setRoleModalMode('create');
    setRoleModalOpen(true);
    setRoleMenuId(null);
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setRoleModalMode('edit');
    setRoleModalOpen(true);
    setRoleMenuId(null);
  };

  const openEditPermission = (permission) => {
    setEditingPermission(permission);
    setPermissionModalOpen(true);
    setPermissionMenuId(null);
  };

  const openRolePermissions = async (role) => {
    setError('');
    setToast({ message: '', type: 'success' });
    setRoleMenuId(null);
    setActiveRoleId(role.id);
    try {
      const data = await loadRoleDetails(role.id);
      setRoleDetails(data);
      setRolePermissionsOpen(true);
    } catch (e) {
      const msg = toErrorText(e, 'Не удалось загрузить доступы роли');
      setError(msg);
      showToast(msg, 'error');
    }
  };

  const submitRole = async (payload) => {
    setSaving(true);
    setError('');
    setToast({ message: '', type: 'success' });
    try {
      const isEdit = roleModalMode === 'edit' && editingRole?.id;
      const url = isEdit
        ? `${API_URL}/admin/access/roles/${editingRole.id}`
        : `${API_URL}/admin/access/roles`;
      const method = isEdit ? 'PATCH' : 'POST';

      const body = isEdit
        ? {
            name: payload.name,
            description: payload.description || null,
          }
        : {
            name: payload.name,
            description: payload.description || null,
          };

      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        throw new Error(await readError(res, 'Не удалось сохранить роль'));
      }

      const saved = await res.json();
      await loadAll();
      setActiveRoleId(saved?.id || activeRoleId);
      setRoleModalOpen(false);
      setEditingRole(null);
      showToast(isEdit ? 'Роль обновлена.' : 'Роль создана.');
    } catch (e) {
      const msg = toErrorText(e, 'Не удалось сохранить роль');
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const submitPermission = async (payload) => {
    if (!editingPermission?.id) return;
    setSaving(true);
    setError('');
    setToast({ message: '', type: 'success' });
    try {
      const body = {
        name: payload.name,
        description: payload.description || null,
        group_name: payload.group_name || null,
      };

      const res = await apiFetch(`${API_URL}/admin/access/permissions/${editingPermission.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        throw new Error(await readError(res, 'Не удалось сохранить доступ'));
      }

      await loadAll();
      if (roleDetails?.id) {
        const freshRole = await loadRoleDetails(roleDetails.id);
        setRoleDetails(freshRole);
      }
      setPermissionModalOpen(false);
      setEditingPermission(null);
      showToast('Доступ обновлён.');
    } catch (e) {
      const msg = toErrorText(e, 'Не удалось сохранить доступ');
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveRolePermissions = async (payload) => {
    if (!roleDetails?.id) return;
    setSaving(true);
    setError('');
    setToast({ message: '', type: 'success' });
    try {
      const res = await apiFetch(`${API_URL}/admin/access/roles/${roleDetails.id}/permissions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(await readError(res, 'Не удалось сохранить доступы роли'));
      }

      const data = await res.json();
      setRoleDetails(data);
      await loadAll();
      setRolePermissionsOpen(false);
      showToast('Доступы роли обновлены.');
    } catch (e) {
      const msg = toErrorText(e, 'Не удалось сохранить доступы роли');
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (role) => {
    if (!role || role.is_system) return;
    if (!window.confirm(`Удалить роль «${role.name}»?`)) return;

    setSaving(true);
    setError('');
    setToast({ message: '', type: 'success' });
    setRoleMenuId(null);

    try {
      const res = await apiFetch(`${API_URL}/admin/access/roles/${role.id}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        throw new Error(await readError(res, 'Не удалось удалить роль'));
      }

      await loadAll();
      showToast('Роль удалена.');
    } catch (e) {
      const msg = toErrorText(e, 'Не удалось удалить роль');
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePermission = async (permission) => {
    if (!permission || permission.is_system) return;
    if (!window.confirm(`Удалить доступ «${permission.name}»?`)) return;

    setSaving(true);
    setError('');
    setToast({ message: '', type: 'success' });
    setPermissionMenuId(null);

    try {
      const res = await apiFetch(`${API_URL}/admin/access/permissions/${permission.id}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        throw new Error(await readError(res, 'Не удалось удалить доступ'));
      }

      await loadAll();
      if (roleDetails?.id) {
        const freshRole = await loadRoleDetails(roleDetails.id);
        setRoleDetails(freshRole);
      }
      showToast('Доступ удалён.');
    } catch (e) {
      const msg = toErrorText(e, 'Не удалось удалить доступ');
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const tabItems = useMemo(() => ([
    { key: 'roles', label: 'Роли' },
    { key: 'permissions', label: 'Доступы' },
  ]), []);

  return (
    <>
      <InnerTabs value={subtab} onChange={setSubtab} items={tabItems} />

      {subtab === 'roles' ? (
        <RolesList
          loading={loading}
          roles={roles}
          search={roleSearch}
          setSearch={setRoleSearch}
          activeRoleId={activeRoleId}
          setActiveRoleId={setActiveRoleId}
          roleMenuId={roleMenuId}
          setRoleMenuId={setRoleMenuId}
          openRolePermissions={openRolePermissions}
          openEditRole={openEditRole}
          handleDeleteRole={handleDeleteRole}
        />
      ) : (
        <PermissionsList
          loading={loading}
          permissions={permissions}
          search={permissionSearch}
          setSearch={setPermissionSearch}
          permissionMenuId={permissionMenuId}
          setPermissionMenuId={setPermissionMenuId}
          openEditPermission={openEditPermission}
          handleDeletePermission={handleDeletePermission}
        />
      )}

      {toast.message ? (
        <div className={`${s.roleToast} ${toast.type === 'error' ? s.roleToastError : s.roleToastSuccess}`}>
          {toast.message}
        </div>
      ) : null}

      {subtab === 'roles' ? (
        <button
          type="button"
          className={s.fab}
          aria-label="Создать роль"
          title="Создать роль"
          onClick={openCreateRole}
        >
          <span className={s.fabSvgWrap}>
            <FabBubble className={s.fabBubble} />
            <span className={s.fabInner}>
              <AddIconPlus className={s.fabIcon} />
            </span>
          </span>
        </button>
      ) : null}

      {roleModalOpen ? (
        <RoleModal
          title={roleModalMode === 'edit' ? 'Редактирование роли' : 'Создание роли'}
          initialValue={editingRole}
          saving={saving}
          onCancel={() => {
            setRoleModalOpen(false);
            setEditingRole(null);
          }}
          onSubmit={submitRole}
        />
      ) : null}

      {permissionModalOpen ? (
        <PermissionModal
          title="Редактирование доступа"
          initialValue={editingPermission}
          permissions={permissions}
          saving={saving}
          onCancel={() => {
            setPermissionModalOpen(false);
            setEditingPermission(null);
          }}
          onSubmit={submitPermission}
        />
      ) : null}

      {rolePermissionsOpen && roleDetails ? (
        <RolePermissionsModal
          role={roleDetails}
          permissions={permissions}
          saving={saving}
          onCancel={() => {
            setRolePermissionsOpen(false);
            setRoleDetails(null);
          }}
          onSubmit={saveRolePermissions}
        />
      ) : null}
    </>
  );
}
